import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import * as actions from './upf-assign-actions';
import { customSort } from '../../components/student-list/student-list-util';
import { MAX_LJ_COUNT } from '../../helpers/utils';
import PortalCommunication from '../../helpers/portal-comms';
import {
  getTopicsAndStandards, updateStrandAndStandards, getFilteredStrandAndStandards,
  updateStrands, removeStandard, updateGridCells,
  updateCount, filterStandardByLJCount
} from './upf-assign-util';
import LocalStorage from '../../helpers/local-storage';

export const initialState = {
  isLoading: false,
  isLoadingStudentData: false,
  classrooms: [],
  filteredStudents: [],
  selectedStudentIds: [],
  studentsAssignmentStatus: [],
  studentsProgress: [],
  sortedStudents: [],
  sortDirection: 'asc',
  sortValue: ['lastName', 'firstName'],
  showGridLoader: true,
  unsavedChanges: false,
  pausePolling: false,
  selectedCells: [],
  publications: [],
  genericCourses: [],
  publicationCourses: [],
  topics: [],
  selectedTopics: [],
  strands: [],
  selectedCourse: -1,
  assignmentStatuses: [],
  showSearchPanel: false,
  displayModalKey: -1,
  studentAssignments: [],
  actionInProgress: false,
  selectedLJIds: [],
  studentUnAssignments: [],
  assessmentTypes: [],
  selectedAssessmentType: -1,
  selectedClassRoom: null
};

export default (state = initialState, action) => {
  switch (action.type) {
    case actions.FILTERED_STUDENTS: {
      const { sortDirection, sortValue } = state;
      return {
        ...state,
        filteredStudents: customSort(action.payload, sortValue, sortDirection),
        showGridLoader: true,
        selectedStudentIds: [],
        studentAssignments: [],
        studentUnAssignments: [],
        selectedLJIds: [],
        pausePolling: false
      };
    }

    case actions.STUDENT_ASSIGNMENTS: {
      const { unsavedChanges } = state;

      if (unsavedChanges) {
        return {
          ...state,
          showGridLoader: false,
          isLoadingStudentData: false,
          unsavedChanges: true,
          isLoading: false
        };
      }
      return {
        ...state,
        assignmentStatuses: action.payload.assignmentStatuses,
        studentsAssignmentStatus: action.payload.assignmentStatuses,
        studentsProgress: action.payload.studentsProgress,
        psrQuestionSummaries: action.payload.psrQuestionSummaries || [],
        showGridLoader: false,
        selectedStudentIds: [],
        selectedPSRs: [],
        isLoadingStudentData: false,
        unsavedChanges: false,
        isLoading: false,
        pausePolling: false,
        studentAssignments: [],
        selectedLJIds: [],
        studentUnAssignments: []
      };
    }

    case actions.UPDATE_SORTED_STUDENTS_LIST: {
      const { sortDirection, sortValue } = state;
      return {
        ...state,
        sortedStudents: customSort(action.students, sortValue, sortDirection)
      };
    }

    case actions.UPDATE_SORT_PROPERTIES: {
      return {
        ...state,
        sortDirection: action.direction,
        sortValue: action.sortValue
      };
    }

    case actions.ERROR_DETECTED: {
      const message = `${'Oops an error has occurred. '}
      ${<a href="http://www.3plearning.com/contact/">Please contact Tech Support</a>}
      ${' with details of what happened and we will work to get you back on track.'}`;
      PortalCommunication.serverError(message);
      return { ...state, isLoading: false };
    }

    case actions.PUBLICATIONS_ACQUIRED: {
      return {
        ...state,
        publications: customSort((action.payload || []), ['country'], 'asc'),
        isLoading: false
      };
    }

    case actions.UPDATE_ASSESSMENT_TYPES: {
      const assessmentTypes = action.assessmentTypes || [];
      return {
        ...state,
        assessmentTypes,
        selectedAssessmentType: assessmentTypes.find(type => type.id === 1) || -1
      };
    }

    case actions.UPDATE_SELECTED_ASSESSMENT_TYPE:
      return {
        ...state,
        selectedAssessmentType: action.selectedAssessmentType || -1
      };

    case actions.GENERIC_COURSES_ACQUIRED: {
      const genericCourses = action.payload || [];
      let topicsAndStandards = [];
      const { id, version } = state.selectedCourse;
      const key = `${version}_${id}_FILTER`;
      const savedFilter = LocalStorage.getItem(key);
      topicsAndStandards = savedFilter || getTopicsAndStandards(genericCourses);
      const selectedTopics = savedFilter ? topicsAndStandards
        : filterStandardByLJCount(topicsAndStandards, MAX_LJ_COUNT);
      return {
        ...state,
        strands: topicsAndStandards,
        topics: selectedTopics,
        selectedTopics,
        studentAssignments: [],
        genericCourses
      };
    }

    case actions.CLASSROOMS_ACQUIRED: {
      PortalCommunication.loadingSpinnerOff();
      return {
        ...state,
        classrooms: action.payload || [],
        isLoading: false
      };
    }

    case actions.UPDATE_STRANDS: {
      const { strands } = state;
      const { id, checked, searchTerm, actionType } = action;
      const clonedStrands = cloneDeep(strands);

      let updatedTopicData = [];
      let updatedStrandAndStandards = [];

      switch (actionType) {
        case 'selection': {
          updatedStrandAndStandards = updateStrandAndStandards(id, checked, clonedStrands);
          if (searchTerm && searchTerm !== '') {
            const clonedData = cloneDeep(updatedStrandAndStandards);
            updatedTopicData = getFilteredStrandAndStandards(searchTerm, clonedData);
          } else {
            updatedTopicData = cloneDeep(updatedStrandAndStandards);
          }
          break;
        }

        case 'uncheckAll':
          updatedStrandAndStandards = updateStrands(false, clonedStrands);
          updatedTopicData = updatedStrandAndStandards;
          break;

        case 'reset':
          updatedStrandAndStandards = updateStrands(true, clonedStrands);
          updatedTopicData = updatedStrandAndStandards;
          break;

        default: {
          updatedStrandAndStandards = strands;
          const clonedStrandData = cloneDeep(updatedStrandAndStandards);
          updatedTopicData = getFilteredStrandAndStandards(searchTerm, clonedStrandData);
        }
          break;
      }

      return {
        ...state,
        strands: updatedStrandAndStandards,
        topics: updatedTopicData
      };
    }

    case actions.APPLY_FILTER: {
      const { selectedCourse, strands } = state;
      const { id, version } = selectedCourse;
      const key = `${version}_${id}_FILTER`;
      const savedFilter = LocalStorage.getItem(key);
      if (savedFilter) {
        LocalStorage.removeItem(key);
      }
      LocalStorage.setItem(key, strands);
      return {
        ...state,
        selectedTopics: strands
      };
    }

    case actions.UPDATE_SELECTED_COURSE: {
      return {
        ...state,
        selectedCourse: action.selectedCourse.course,
        studentAssignments: [],
        selectedStudentIds: [],
        selectedLJIds: [],
        studentUnAssignments: [],
        pausePolling: false
      };
    }

    case actions.SHOW_HIDE_SEARCH_PANEL: {
      const { strands } = state;
      strands.forEach((strand) => {
        strand.isExpand = !!strand.childTopics.find(s => s.selected);
      });
      return {
        ...state,
        showSearchPanel: action.status,
        strands,
        topics: strands
      };
    }

    case actions.DISPLAY_MODAL_INFORMATION: {
      return {
        ...state,
        displayModalKey: action.key
      };
    }

    case actions.HIDE_STANDARD: {
      const { selectedTopics, topics, studentAssignments, studentUnAssignments } = state;
      const updatedSelectedTopics = removeStandard(action.id, selectedTopics);
      const updatedStudentAssignment = updateCount(action.id, topics, studentAssignments);
      const updatedStudentUnAssignment = updateCount(action.id, topics, studentUnAssignments);
      return {
        ...state,
        topics: updatedSelectedTopics,
        selectedTopics: updatedSelectedTopics,
        studentAssignments: updatedStudentAssignment,
        studentUnAssignments: updatedStudentUnAssignment,
        pausePolling: (updatedStudentAssignment.length > 0) || (updatedStudentUnAssignment.length > 0)
      };
    }

    case actions.ASSIGN_REQUEST: {
      return {
        ...state,
        actionInProgress: true
      };
    }

    case actions.ASSIGN_SUCCESS_REQUEST: {
      return {
        ...state,
        actionInProgress: false,
        studentAssignments: [],
        studentUnAssignments: [],
        selectedStudentIds: [],
        selectedLJIds: []
      };
    }

    case actions.ASSIGN_FAILURE_REQUEST: {
      return {
        ...state,
        actionInProgress: false
      };
    }

    case actions.GRID_CELL_SELECTION: {
      const { studentId, status, learningJourneyIds } = action;
      const {
        assignmentStatuses,
        studentAssignments,
        selectedLJIds,
        selectedStudentIds,
        studentUnAssignments
      } = state;
      const response = updateGridCells(
        studentId, assignmentStatuses,
        studentAssignments, status, selectedLJIds, selectedStudentIds, learningJourneyIds, studentUnAssignments);
      return {
        ...state,
        actionInProgress: false,
        assignmentStatuses: response.assignmentStatuses,
        studentAssignments: response.studentAssignments,
        selectedStudentIds: response.clonedStudentIds,
        selectedLJIds: response.clonedSelectedLJIDs,
        studentUnAssignments: response.studentUnAssignments,
        pausePolling: response.studentAssignments.length > 0 || response.studentUnAssignments.length > 0
      };
    }

    case actions.UNASSIGN_REQUEST: {
      return {
        ...state,
        actionInProgress: true
      };
    }

    case actions.UNASSIGN_SUCCESS_REQUEST: {
      return {
        ...state,
        actionInProgress: false,
        studentUnAssignments: [],
        studentAssignments: []
      };
    }

    case actions.UNASSIGN_FAILURE_REQUEST: {
      return {
        ...state,
        actionInProgress: false
      };
    }

    case actions.EXPAND_COLLAPSE_REQUEST: {
      const { topics } = state;
      const topic = topics[0].childTopics.find(t => t.id === action.strandId);
      topic.isExpand = !topic.isExpand;
      return {
        ...state,
        topics
      };
    }

    case actions.UPDATE_SELECTED_CLASSROOM:
      return {
        ...state,
        selectedClassRoom: action.selectedClassRoom
      };

    case actions.UPDATE_SELECTED_GROUP:
      return {
        ...state,
        selectedGroup: action.selectedGroup
      };

    case actions.PAUSE_POLLING:
      return {
        ...state,
        pausePolling: true
      };

    case actions.RESUME_POLLING:
      return {
        ...state,
        pausePolling: false
      };

    default:
      return state;
  }
};
